/**
 * The route solver.
 *
 * A pure function. No Prisma, no `fetch`, no `new Date()` in the arithmetic —
 * it takes stops, vehicles and a travel-lookup function, and returns a plan.
 * That is what makes a claim like "this saves forty minutes" something a test
 * can hold the solver to rather than something a demo asserts.
 *
 * It is also deterministic. The same inputs give the same plan, every time, on
 * every machine. That matters more here than the last two percent of quality:
 * a dispatcher who re-runs the optimiser and gets a different answer stops
 * believing either one. So there is no randomness, ties break on stop id, and
 * the improvement loop is bounded by a move count rather than a wall clock.
 *
 * ## The shape of the problem
 *
 * A vehicle routing problem with time windows, but four details make it this
 * business's problem rather than a textbook one:
 *
 * - **Technicians start and finish at home.** Not a depot. The last hop of the
 *   day is the one that decides whether the final job is a kindness or a
 *   punishment, and a solver that ignores it will happily finish somebody in
 *   Rockingham who lives in Joondalup.
 * - **Travel is time-dependent and directional.** `travelAt(from, to, departAt)`
 *   rather than `matrix[i][j]`, so entering the city at 7am and leaving it at
 *   4pm are priced as the different journeys they are. Every move that shifts a
 *   stop's time re-prices the hops around it, which is most of why the local
 *   search below is written the way it is.
 * - **Some stops cannot move, on three independent axes.** A promise to a
 *   customer comes at three strengths, and the solver honours all three.
 * - **The van runs out.** Load accumulates along a run and the office is a stop
 *   the solver can insert to reset it.
 *
 * ## What it does not do
 *
 * It does not write anything. It returns a proposal, and a dispatcher applies
 * it. It also does not pretend to be exact: this is insertion plus local
 * search, which for a day of thirty stops across six vans lands within a few
 * percent of optimal in well under a second. An exact solver for a problem this
 * shape is a research project, and the residual few percent is smaller than the
 * error in the duration estimates it is routing.
 */

export interface Coord {
  latitude: number
  longitude: number
}

/**
 * The cost of a hop, at a time.
 *
 * Supplied by the caller — `prepareTravel` in the API builds one backed by
 * Google Routes and a cache, and tests pass a scripted one. Synchronous
 * because the improvement loop calls it tens of thousands of times.
 */
export type TravelAt = (from: Coord, to: Coord, departAt: Date) => TravelCost

export interface TravelCost {
  minutes: number
  metres: number
}

/** Why a stop is pinned, or `null` when the solver is free to move it. */
export type StopFixing = 'anchor' | 'time' | 'day' | null

export interface RouteStop {
  /** Visit id for booked work, job id for something being pulled off the backlog. */
  id: string
  jobId: string
  /** Null when the address has never been geocoded. Such a stop cannot be routed. */
  coord: Coord | null
  serviceMinutes: number

  /**
   * Already happening, or already happened.
   *
   * `EN_ROUTE`, `ON_SITE` and `COMPLETED` visits. Not a lock — a lock is a
   * promise, this is a fact — but the solver treats both as immovable, and the
   * route has to drive around it either way.
   */
  anchored: boolean

  lockDay: boolean
  lockTime: boolean
  lockTech: boolean

  /** The current booking. Null on a stop being pulled in from the backlog. */
  startAt: Date | null
  endAt: Date | null
  userId: string | null

  /** Higher sorts first when something has to give. */
  priority: number
  dueAt: Date | null
  /** Domestic, commercial. A soft preference, never a hard constraint. */
  scheduleGroupId: string | null
  /**
   * The service area the site is in, from its suburb or postcode (ADR 021).
   * Null or absent is "no area", which costs nothing anywhere.
   */
  areaId?: string | null

  /** Van load this consumes, and whether it needs a pickup from the office first. */
  restockUnits: number
  requiresPickup: boolean
}

export interface Vehicle {
  userId: string
  /** Falls back to the branch, then to the first stop. Null routes without anchors. */
  home: Coord | null
  /** Null is uncapacitated: a van nobody has measured never gets sent to the office. */
  capacityUnits: number | null
  shiftStart: Date
  shiftEnd: Date
  /**
   * Leave, meetings and breaks. The route drives around these rather than
   * through them, and they are not stops — nothing is delivered at one.
   */
  obstacles: Array<{ startAt: Date; endAt: Date }>
  scheduleGroupId: string | null
  /** The service areas this person covers. Empty or absent covers none. */
  areaIds?: readonly string[]
}

/**
 * What the objective trades off against what.
 *
 * All in units of "minutes of driving this is worth avoiding", so the numbers
 * are comparable and the weighting is legible. Every default is a judgement and
 * they are meant to be argued with once the business has seen a few plans.
 */
export interface Weights {
  /** A minute past the end of the shift. Worse than a minute of driving. */
  overtimeMinute: number
  /** A minute past a job's `dueAt`, multiplied by priority. */
  latenessMinute: number
  /** Leaving a stop unplaced. Deliberately large: a plan that drops work is not a plan. */
  unassigned: number
  /** Each detour through the office. The drive is counted separately; this is the interruption. */
  restockStop: number
  /** A domestic job on the commercial technician. Soft, so geography can still win. */
  groupMismatch: number
  /**
   * A job on a technician who does not cover its area, when somebody in the
   * plan does. Nobody pays it for an area whose people are all off that day.
   * Soft: an emergency next door to the wrong technician still goes to them.
   */
  outsideArea: number
  /**
   * Each service area past the first in one technician's day. This is what
   * keeps a day in one part of the map when drive times alone are a tie.
   */
  extraArea: number
}

export const DEFAULT_WEIGHTS: Weights = {
  overtimeMinute: 3,
  latenessMinute: 2,
  unassigned: 240,
  restockStop: 10,
  groupMismatch: 15,
  outsideArea: 20,
  extraArea: 25,
}

export interface SolveOptions {
  travelAt: TravelAt
  /** The branch. Null means replenishment is impossible and capacity cannot be enforced. */
  office: Coord | null
  /** Door to door for a call past the office, from `Setting.restockMinutes`. */
  restockMinutes: number
  weights?: Partial<Weights>
  /**
   * Improvement moves to consider before stopping.
   *
   * A move count rather than milliseconds, so the plan does not depend on how
   * busy the machine was. Sized so a normal day exhausts the neighbourhood long
   * before reaching it; it is a guard against a pathological input, not a knob.
   */
  maxMoves?: number
  /**
   * Place new work only where it fits inside the shift.
   *
   * New work is a stop with no current booking — a job pulled off the
   * backlog. By default overtime is a price, which is right for one late job
   * on the phone and wrong for a planner offering several days: every job
   * would land on the first day at time-and-a-half and the second day would
   * stay empty. With this on, bookings already on the day are placed first,
   * exactly as without it, and new work goes only where it adds no overtime
   * and does not run into a booking pinned to its time. What fits nowhere is
   * `no-room`, so the caller can offer it to another day.
   */
  newWorkWithinShift?: boolean
}

export const DEFAULT_MAX_MOVES = 40_000

/** A planned arrival, which is either a job or a call past the office. */
export interface PlannedStop {
  kind: 'job' | 'restock'
  /** Stop id for a job; a synthetic `restock:<n>` for an office call. */
  id: string
  jobId: string | null
  startAt: Date
  endAt: Date
  /** The drive that got here. Zero for the first thing after leaving home. */
  travelMinutes: number
  travelMetres: number
  /** Where this stop is now, against where it was. Nothing moved means nothing to apply. */
  moved: boolean
  /**
   * Where it was, so a dispatcher can be shown what they are agreeing to.
   *
   * Null on a restock and on a job being pulled off the backlog, both of which
   * have no previous booking. Carried here rather than left for the client to
   * rejoin against the board, because the diff has to be the same comparison
   * the solver made — the board may already have moved on.
   */
  wasStartAt: Date | null
  wasUserId: string | null
}

export interface PlannedRoute {
  userId: string
  stops: PlannedStop[]
  driveMinutes: number
  driveMetres: number
  /** Minutes past `shiftEnd`, zero when the day fits. */
  overtimeMinutes: number
  /** When they get home, which is the number a technician actually cares about. */
  homeAt: Date | null
}

/**
 * A stop the solver could not place, and why.
 *
 * Structured rather than a message, because this is what the advisory pass
 * reads. "Could not fit" gives an LLM nothing to reason about; "needs 95
 * minutes, the largest remaining gap is 60, and the two candidates are locked
 * to Tuesday" gives it a trade-off to describe.
 */
export interface Infeasible {
  stopId: string
  jobId: string
  reason:
    | 'no-coordinates'
    | 'no-vehicle'
    | 'no-room'
    | 'locked-conflict'
    | 'exceeds-van-capacity'
    | 'needs-pickup-no-office'
  detail: string
  /** How much room would have been needed, where that is a number. */
  shortfallMinutes?: number
}

export interface RoutePlanResult {
  routes: PlannedRoute[]
  unassigned: Infeasible[]
  metrics: {
    driveMinutes: number
    driveMetres: number
    /** Drive time in the bookings as they stand, for the "saves 40 minutes" line. */
    baselineDriveMinutes: number
    overtimeMinutes: number
    restockStops: number
    /** How many bookings the dispatcher would be agreeing to change. */
    moved: number
    cost: number
  }
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function solveRoutes(
  stops: RouteStop[],
  vehicles: Vehicle[],
  options: SolveOptions,
): RoutePlanResult {
  const weights = { ...DEFAULT_WEIGHTS, ...options.weights }
  const context: Context = {
    travelAt: options.travelAt,
    office: options.office,
    restockMinutes: options.restockMinutes,
    weights,
    coveredAreas: new Set(vehicles.flatMap((vehicle) => vehicle.areaIds ?? [])),
  }

  const unassigned: Infeasible[] = []

  // Sorted by id throughout. Determinism is not a nice-to-have here: two runs
  // that disagree about a tie would produce two different plans for one day.
  const ordered = [...stops].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const byUser = new Map(vehicles.map((vehicle) => [vehicle.userId, vehicle]))

  const routable: RouteStop[] = []

  for (const stop of ordered) {
    if (!stop.coord) {
      unassigned.push({
        stopId: stop.id,
        jobId: stop.jobId,
        reason: 'no-coordinates',
        detail: 'The site address has never been geocoded, so it cannot be placed on a route.',
      })
      continue
    }

    if (stop.requiresPickup && !options.office) {
      unassigned.push({
        stopId: stop.id,
        jobId: stop.jobId,
        reason: 'needs-pickup-no-office',
        detail: 'This job needs a pickup, and no branch address is set to collect it from.',
      })
      continue
    }

    routable.push(stop)
  }

  // Sequences, not schedules. A route is an order of stops; turning one into
  // times is `schedule()`, and it happens fresh on every evaluation because the
  // times are what the travel cost depends on.
  const sequences = new Map<string, RouteStop[]>(vehicles.map((v) => [v.userId, []]))

  const free: RouteStop[] = []

  for (const stop of routable) {
    const pinned = fixingOf(stop)

    if (pinned === null && !stop.lockTech) {
      free.push(stop)
      continue
    }

    // Anchored or locked to a person: it belongs to that person's route and
    // nothing here may take it off them.
    const vehicle = stop.userId ? byUser.get(stop.userId) : undefined

    if (!vehicle) {
      unassigned.push({
        stopId: stop.id,
        jobId: stop.jobId,
        reason: stop.userId ? 'locked-conflict' : 'no-vehicle',
        detail: stop.userId
          ? 'Locked to a technician who is not working in this plan.'
          : 'Pinned in place but has nobody assigned.',
      })
      continue
    }

    sequences.get(vehicle.userId)!.push(stop)
  }

  // Pinned stops start in time order. A locked stop has a time; an anchored one
  // is happening now. Either way their relative order is already decided.
  for (const sequence of sequences.values()) {
    sequence.sort(byStart)
  }

  const guard = options.newWorkWithinShift ?? false
  if (guard) {
    insertByRegret(free.filter((stop) => !isNewWork(stop)), sequences, byUser, context, unassigned, false)
    insertByRegret(free.filter(isNewWork), sequences, byUser, context, unassigned, true)
  } else {
    insertByRegret(free, sequences, byUser, context, unassigned, false)
  }
  improve(sequences, byUser, context, options.maxMoves ?? DEFAULT_MAX_MOVES, guard)

  // ---- Build the result -------------------------------------------------

  const routes: PlannedRoute[] = []
  let driveMinutes = 0
  let driveMetres = 0
  let overtimeMinutes = 0
  let restockStops = 0
  let moved = 0

  for (const vehicle of vehicles) {
    const sequence = sequences.get(vehicle.userId) ?? []
    const run = schedule(sequence, vehicle, context)

    for (const failure of run.failures) unassigned.push(failure)

    const stops = run.stops
    routes.push({
      userId: vehicle.userId,
      stops,
      driveMinutes: run.driveMinutes,
      driveMetres: run.driveMetres,
      overtimeMinutes: run.overtimeMinutes,
      homeAt: run.homeAt,
    })

    driveMinutes += run.driveMinutes
    driveMetres += run.driveMetres
    overtimeMinutes += run.overtimeMinutes
    restockStops += stops.filter((stop) => stop.kind === 'restock').length
    moved += stops.filter((stop) => stop.moved).length
  }

  return {
    routes,
    unassigned,
    metrics: {
      driveMinutes,
      driveMetres,
      baselineDriveMinutes: baselineDrive(routable, vehicles, context),
      overtimeMinutes,
      restockStops,
      moved,
      cost: totalCost(sequences, vehicles, context, unassigned.length),
    },
  }
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface Context {
  travelAt: TravelAt
  office: Coord | null
  restockMinutes: number
  weights: Weights
  /** Areas somebody in this plan covers. */
  coveredAreas: ReadonlySet<string>
}

function fixingOf(stop: RouteStop): StopFixing {
  if (stop.anchored) return 'anchor'
  if (stop.lockTime) return 'time'
  if (stop.lockDay) return 'day'
  return null
}

/** A stop with no current booking: work being pulled onto the day. */
function isNewWork(stop: RouteStop): boolean {
  return stop.startAt == null
}

/**
 * Whether a route got worse in the two ways `newWorkWithinShift` forbids:
 * later home than the shift allows, or arriving after a pinned booking's time.
 */
function breaksShift(before: ScheduledRun, after: ScheduledRun): boolean {
  return after.overtimeMinutes > before.overtimeMinutes || after.clashMinutes > before.clashMinutes
}

function byStart(a: RouteStop, b: RouteStop): number {
  const left = a.startAt?.getTime() ?? Number.MAX_SAFE_INTEGER
  const right = b.startAt?.getTime() ?? Number.MAX_SAFE_INTEGER
  if (left !== right) return left - right
  return a.id < b.id ? -1 : 1
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000)
}

function minutesBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 60_000)
}

interface ScheduledRun {
  stops: PlannedStop[]
  driveMinutes: number
  driveMetres: number
  overtimeMinutes: number
  latenessMinutes: number
  mismatches: number
  /** Jobs in an area the technician does not cover. */
  outsideAreas: number
  /** Distinct service areas in the day, past the first. */
  extraAreas: number
  /**
   * Minutes the route would have arrived after a booking pinned to its time.
   * The booking keeps its slot regardless, so this is an overlap on the board.
   * Not priced; read only by `newWorkWithinShift`.
   */
  clashMinutes: number
  homeAt: Date | null
  failures: Infeasible[]
  feasible: boolean
}

/**
 * Turn an order of stops into times, inserting office calls where the van runs
 * out. This is the only place that knows what a route actually costs.
 *
 * Walks forward from the shift start. Each hop is priced at the moment it is
 * driven, which is the whole reason this cannot be a lookup table: move a stop
 * and every hop after it happens at a different time.
 */
function schedule(sequence: RouteStop[], vehicle: Vehicle, context: Context): ScheduledRun {
  const stops: PlannedStop[] = []
  const failures: Infeasible[] = []

  let driveMinutes = 0
  let driveMetres = 0
  let latenessMinutes = 0
  let mismatches = 0
  let outsideAreas = 0
  const areas = new Set<string>()
  let clashMinutes = 0
  let feasible = true

  let clock = vehicle.shiftStart
  let position = vehicle.home
  let load = 0
  let restocks = 0

  const capacity = vehicle.capacityUnits
  const covers = vehicle.areaIds ?? []

  for (const stop of sequence) {
    const coord = stop.coord!

    // Does the van have to call past the office before this one? Two triggers,
    // and they are genuinely different: one is about how full the van is, the
    // other about what is in it.
    const overCapacity = capacity != null && load + stop.restockUnits > capacity
    const needsPickup = stop.requiresPickup && restocks === 0 && !startedLoaded(stop, capacity)

    if ((overCapacity || needsPickup) && context.office) {
      if (capacity != null && stop.restockUnits > capacity) {
        // A single job bigger than the van. No amount of reordering fixes this;
        // it is a conversation about the van or about splitting the job.
        failures.push({
          stopId: stop.id,
          jobId: stop.jobId,
          reason: 'exceeds-van-capacity',
          detail: `Needs ${stop.restockUnits} load units and the van carries ${capacity}.`,
        })
        feasible = false
        continue
      }

      const hop = position
        ? context.travelAt(position, context.office, clock)
        : { minutes: 0, metres: 0 }

      clock = addMinutes(clock, hop.minutes)
      driveMinutes += hop.minutes
      driveMetres += hop.metres

      clock = clearObstacles(clock, context.restockMinutes, vehicle.obstacles)
      const restockEnd = addMinutes(clock, context.restockMinutes)

      stops.push({
        kind: 'restock',
        id: `restock:${restocks}`,
        jobId: null,
        startAt: clock,
        endAt: restockEnd,
        travelMinutes: hop.minutes,
        travelMetres: hop.metres,
        moved: true,
        wasStartAt: null,
        wasUserId: null,
      })

      clock = restockEnd
      position = context.office
      load = 0
      restocks += 1
    }

    const hop = position ? context.travelAt(position, coord, clock) : { minutes: 0, metres: 0 }

    driveMinutes += hop.minutes
    driveMetres += hop.metres

    let arriveAt = addMinutes(clock, hop.minutes)

    // A locked time is a promise, so it wins over arithmetic: the booking keeps
    // its slot and the route is early or late around it. Reporting the clash is
    // the router's job; moving the booking is not.
    const pinnedAt = (stop.lockTime || stop.anchored) && stop.startAt ? stop.startAt : null
    if (pinnedAt) {
      if (arriveAt > pinnedAt) clashMinutes += minutesBetween(pinnedAt, arriveAt)
      arriveAt = pinnedAt
    } else arriveAt = clearObstacles(arriveAt, stop.serviceMinutes, vehicle.obstacles)

    const endAt = addMinutes(arriveAt, stop.serviceMinutes)

    if (stop.dueAt && endAt > stop.dueAt) {
      latenessMinutes += minutesBetween(stop.dueAt, endAt) * Math.max(1, stop.priority)
    }

    if (
      stop.scheduleGroupId &&
      vehicle.scheduleGroupId &&
      stop.scheduleGroupId !== vehicle.scheduleGroupId
    ) {
      mismatches += 1
    }

    if (stop.areaId) {
      areas.add(stop.areaId)
      if (context.coveredAreas.has(stop.areaId) && !covers.includes(stop.areaId)) outsideAreas += 1
    }

    stops.push({
      kind: 'job',
      id: stop.id,
      jobId: stop.jobId,
      startAt: arriveAt,
      endAt,
      travelMinutes: hop.minutes,
      travelMetres: hop.metres,
      moved:
        stop.userId !== vehicle.userId ||
        stop.startAt == null ||
        stop.startAt.getTime() !== arriveAt.getTime(),
      wasStartAt: stop.startAt,
      wasUserId: stop.userId,
    })

    clock = endAt
    position = coord
    load += stop.restockUnits
  }

  // The drive home. Counted, because leaving it out is how a solver finishes
  // somebody two suburbs past their own street and calls it efficient.
  let homeAt: Date | null = null
  if (position && vehicle.home && stops.length > 0) {
    const hop = context.travelAt(position, vehicle.home, clock)
    driveMinutes += hop.minutes
    driveMetres += hop.metres
    homeAt = addMinutes(clock, hop.minutes)
  }

  const finish = homeAt ?? clock
  const overtimeMinutes = Math.max(0, minutesBetween(vehicle.shiftEnd, finish))

  return {
    stops,
    driveMinutes,
    driveMetres,
    overtimeMinutes,
    latenessMinutes,
    mismatches,
    outsideAreas,
    extraAreas: Math.max(0, areas.size - 1),
    clashMinutes,
    homeAt,
    failures,
    feasible,
  }
}

/**
 * Whether a pickup job can be served from the morning's load.
 *
 * It cannot, by definition — `requiresPickup` means the item is not normally
 * carried. Kept as a named predicate rather than a literal `false` because this
 * is precisely the assumption that changes the day the business starts tracking
 * what is actually in a van, and it should be one edit when it does.
 */
function startedLoaded(_stop: RouteStop, _capacity: number | null): boolean {
  return false
}

/**
 * Push a start time past anything the person is unavailable for.
 *
 * Leave and meetings are obstacles rather than stops: nothing is delivered at
 * one, but the route cannot be inside one either. Repeats because clearing one
 * obstacle can land inside the next.
 */
function clearObstacles(
  start: Date,
  durationMinutes: number,
  obstacles: Array<{ startAt: Date; endAt: Date }>,
): Date {
  let at = start

  for (let pass = 0; pass < obstacles.length + 1; pass += 1) {
    const end = addMinutes(at, durationMinutes)
    const clash = obstacles.find((entry) => entry.startAt < end && at < entry.endAt)
    if (!clash) return at
    at = clash.endAt
  }

  return at
}

/**
 * What a route costs, in comparable minutes.
 *
 * Everything the objective cares about collapses to one number here so the
 * local search below can be a plain "is this smaller" comparison.
 */
function costOf(run: ScheduledRun, context: Context): number {
  const { weights } = context
  return (
    run.driveMinutes +
    run.overtimeMinutes * weights.overtimeMinute +
    run.latenessMinutes * weights.latenessMinute +
    run.stops.filter((stop) => stop.kind === 'restock').length * weights.restockStop +
    run.mismatches * weights.groupMismatch +
    run.outsideAreas * weights.outsideArea +
    run.extraAreas * weights.extraArea +
    (run.feasible ? 0 : weights.unassigned)
  )
}

function routeCost(sequence: RouteStop[], vehicle: Vehicle, context: Context): number {
  return costOf(schedule(sequence, vehicle, context), context)
}

/**
 * Regret-2 cheapest insertion.
 *
 * Plain cheapest-insertion places whatever is cheapest right now, which
 * reliably strands the awkward stop — the one far from everything — until the
 * end, when every route is full and it has to be dropped or driven to
 * specially. Regret-2 asks a better question: not "what is cheapest to place"
 * but "what will hurt most if I do not place it now", measured as the gap
 * between a stop's best option and its second best. The far-flung stop has one
 * good home and a terrible alternative, so it goes first, while it still can.
 */
function insertByRegret(
  free: RouteStop[],
  sequences: Map<string, RouteStop[]>,
  byUser: Map<string, Vehicle>,
  context: Context,
  unassigned: Infeasible[],
  /** Refuse any position that adds overtime or clashes with a pinned time. */
  withinShift: boolean,
): void {
  const pending = [...free]

  while (pending.length > 0) {
    let choice: {
      stop: RouteStop
      userId: string
      index: number
      regret: number
      delta: number
    } | null = null

    for (const stop of pending) {
      let best: { userId: string; index: number; delta: number } | null = null
      let second = Number.POSITIVE_INFINITY

      for (const [userId, sequence] of sequences) {
        const vehicle = byUser.get(userId)
        if (!vehicle) continue

        const beforeRun = schedule(sequence, vehicle, context)
        const before = costOf(beforeRun, context)

        // Only positions that keep the pinned stops in their order are legal.
        for (let index = 0; index <= sequence.length; index += 1) {
          if (!insertionAllowed(sequence, index, stop)) continue

          const candidate = [...sequence]
          candidate.splice(index, 0, stop)
          const run = schedule(candidate, vehicle, context)
          if (withinShift && breaksShift(beforeRun, run)) continue
          const delta = costOf(run, context) - before

          if (!best || delta < best.delta || (delta === best.delta && userId < best.userId)) {
            if (best) second = Math.min(second, best.delta)
            best = { userId, index, delta }
          } else if (delta < second) {
            second = delta
          }
        }
      }

      if (!best) continue

      // No alternative at all is maximum regret: place it now or never.
      const regret = second === Number.POSITIVE_INFINITY ? Number.MAX_SAFE_INTEGER : second - best.delta

      if (
        !choice ||
        regret > choice.regret ||
        (regret === choice.regret && best.delta < choice.delta) ||
        (regret === choice.regret && best.delta === choice.delta && stop.id < choice.stop.id)
      ) {
        choice = { stop, userId: best.userId, index: best.index, regret, delta: best.delta }
      }
    }

    if (!choice) break

    sequences.get(choice.userId)!.splice(choice.index, 0, choice.stop)
    pending.splice(pending.indexOf(choice.stop), 1)
  }

  for (const stop of pending) {
    unassigned.push({
      stopId: stop.id,
      jobId: stop.jobId,
      reason: 'no-room',
      detail: 'No technician has a gap this job fits inside on this day.',
      shortfallMinutes: stop.serviceMinutes,
    })
  }
}

/**
 * Whether a stop may go at this index without disturbing what is pinned.
 *
 * An anchored or time-locked stop keeps its clock position, so nothing may be
 * inserted that would have to be served after a later-locked stop but sits
 * before it in the sequence. Checking the neighbours is enough: the pinned
 * stops are already in time order, so a position legal against both sides is
 * legal against all of them.
 */
function insertionAllowed(sequence: RouteStop[], index: number, stop: RouteStop): boolean {
  if (stop.lockTech && stop.userId) {
    // Handled by never offering this stop to another route, but a caller could
    // reach here through or-opt; refuse rather than trust.
    const owner = sequence.find((entry) => entry.userId)?.userId
    if (owner && owner !== stop.userId) return false
  }

  const previous = sequence[index - 1]
  const next = sequence[index]

  if (previous && fixingOf(previous) && next && fixingOf(next)) {
    // Squeezing between two pinned stops is allowed — whether it actually fits
    // is `schedule`'s answer, priced as lateness or overtime, not a hard no.
    return true
  }

  return true
}

/**
 * Local search: or-opt and 2-opt, until nothing improves.
 *
 * Insertion produces a decent route and a slightly silly one. These two moves
 * clean up the two ways it goes wrong: 2-opt un-crosses a route that doubles
 * back on itself, and or-opt relocates a run of one to three stops that ended
 * up on the wrong van or in the wrong part of the day.
 *
 * Every candidate is re-scheduled from scratch rather than repaired
 * incrementally. That is more work per move and it is the only honest way to do
 * it here: moving a stop changes when every later hop is driven, so a delta
 * computed from the two broken edges would be measuring a route nobody drives.
 *
 * First-improvement rather than best-improvement, and the loop restarts after
 * each accepted move. Best-improvement evaluates the whole neighbourhood to
 * find a marginally better step and is markedly slower for no better answer.
 */
function improve(
  sequences: Map<string, RouteStop[]>,
  byUser: Map<string, Vehicle>,
  context: Context,
  maxMoves: number,
  /**
   * `newWorkWithinShift`: a route carrying new work may not gain overtime or a
   * clash, so the search cannot undo what the guarded insertion refused.
   */
  withinShift: boolean,
): void {
  let moves = 0
  let improved = true

  /** Whether a candidate route is ruled out by `withinShift`, against the route it replaces. */
  const refused = (before: RouteStop[], candidate: RouteStop[], vehicle: Vehicle) =>
    withinShift &&
    candidate.some(isNewWork) &&
    breaksShift(schedule(before, vehicle, context), schedule(candidate, vehicle, context))

  const userIds = [...sequences.keys()].sort()

  while (improved && moves < maxMoves) {
    improved = false

    // ---- 2-opt, within one route ----------------------------------------
    for (const userId of userIds) {
      const vehicle = byUser.get(userId)
      const sequence = sequences.get(userId)
      if (!vehicle || !sequence || sequence.length < 4) continue

      let current = routeCost(sequence, vehicle, context)

      for (let i = 0; i < sequence.length - 1 && moves < maxMoves; i += 1) {
        for (let k = i + 1; k < sequence.length && moves < maxMoves; k += 1) {
          // Reversing a span that contains a pinned stop would move it.
          if (sequence.slice(i, k + 1).some((stop) => fixingOf(stop))) continue

          const candidate = [
            ...sequence.slice(0, i),
            ...sequence.slice(i, k + 1).reverse(),
            ...sequence.slice(k + 1),
          ]

          moves += 1
          const cost = routeCost(candidate, vehicle, context)
          if (cost < current - 1e-9 && !refused(sequence, candidate, vehicle)) {
            sequences.set(userId, candidate)
            sequence.splice(0, sequence.length, ...candidate)
            current = cost
            improved = true
          }
        }
      }
    }

    // ---- Or-opt, within and across routes --------------------------------
    for (const fromId of userIds) {
      const fromVehicle = byUser.get(fromId)
      const fromSequence = sequences.get(fromId)
      if (!fromVehicle || !fromSequence) continue

      for (let length = 1; length <= 3 && moves < maxMoves; length += 1) {
        for (let start = 0; start + length <= fromSequence.length && moves < maxMoves; start += 1) {
          const run = fromSequence.slice(start, start + length)
          if (run.some((stop) => fixingOf(stop))) continue

          const without = [
            ...fromSequence.slice(0, start),
            ...fromSequence.slice(start + length),
          ]

          const fromBefore = routeCost(fromSequence, fromVehicle, context)
          const fromAfter = routeCost(without, fromVehicle, context)

          for (const toId of userIds) {
            const toVehicle = byUser.get(toId)
            const toSequence = sequences.get(toId)
            if (!toVehicle || !toSequence) continue

            // A stop locked to its technician does not change hands.
            if (toId !== fromId && run.some((stop) => stop.lockTech)) continue

            const base = toId === fromId ? without : toSequence
            const toBefore = toId === fromId ? fromAfter : routeCost(toSequence, toVehicle, context)

            for (let index = 0; index <= base.length && moves < maxMoves; index += 1) {
              if (toId === fromId && index === start) continue

              const candidate = [...base]
              candidate.splice(index, 0, ...run)

              moves += 1
              const toAfter = routeCost(candidate, toVehicle, context)

              const delta =
                toId === fromId
                  ? toAfter - fromBefore
                  : fromAfter + toAfter - (fromBefore + toBefore)

              if (delta < -1e-9 && !refused(toId === fromId ? fromSequence : toSequence, candidate, toVehicle)) {
                if (toId === fromId) {
                  fromSequence.splice(0, fromSequence.length, ...candidate)
                } else {
                  fromSequence.splice(0, fromSequence.length, ...without)
                  toSequence.splice(0, toSequence.length, ...candidate)
                }
                improved = true
                break
              }
            }

            if (improved) break
          }

          if (improved) break
        }

        if (improved) break
      }

      if (improved) break
    }
  }
}

/**
 * Drive time in the day as it is booked now.
 *
 * The comparison the dispatcher is actually making. Measured with the same
 * travel function and the same home hops as the proposal, because a saving
 * computed against a differently-measured baseline is a marketing number.
 */
function baselineDrive(stops: RouteStop[], vehicles: Vehicle[], context: Context): number {
  let total = 0

  for (const vehicle of vehicles) {
    const booked = stops
      .filter((stop) => stop.userId === vehicle.userId && stop.startAt)
      .sort(byStart)

    if (booked.length === 0) continue

    let position = vehicle.home
    let clock = vehicle.shiftStart

    for (const stop of booked) {
      if (position) {
        const hop = context.travelAt(position, stop.coord!, clock)
        total += hop.minutes
      }
      position = stop.coord!
      clock = stop.endAt ?? addMinutes(stop.startAt!, stop.serviceMinutes)
    }

    if (position && vehicle.home) {
      total += context.travelAt(position, vehicle.home, clock).minutes
    }
  }

  return total
}

function totalCost(
  sequences: Map<string, RouteStop[]>,
  vehicles: Vehicle[],
  context: Context,
  unassignedCount: number,
): number {
  let total = unassignedCount * context.weights.unassigned

  for (const vehicle of vehicles) {
    total += routeCost(sequences.get(vehicle.userId) ?? [], vehicle, context)
  }

  return total
}
